/**
 * Created by neo on 10.01.21.
 */
import { makeAutoObservable } from 'mobx';
import { LocalizedValue, createLocalizedValue } from '../LocalizedValue';
import { ExploreCategory } from './ExploreCategory';
import { Media, createMedia } from '../Media/Media';
import { NotificationMessage, createNotificationMessage } from './NotificationMessage';
import { translate } from '../../Store/System/LocalizationStore';
import { ExploreEntryQueryRequest } from './ExploreEntryQueryRequest';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { v1ParamsToV3Params } from './exploreV3Params';
import { ExploreContent, createExploreContent } from './ExploreContent';
import { ExploreEntryOrientation } from './ExploreEntryOrientation';
import { ExploreEntryType } from './ExploreEntryType';

export type ExploreEntry = ExploreContent & {
  type: ExploreEntryType;
  backgroundImage?: Media;
  backgroundVideos: Media[];
  notificationMessages: NotificationMessage[];
  quotes: LocalizedValue[];
  orientation: ExploreEntryOrientation;
  categoryData?: ExploreCategory[];
  spotifyPlaylistLink?: string;
  visible: boolean;
  readonly tagString: string;
  readonly media: Media | undefined;
  getNotificationTitle(language: string, tags: string[]): string;
  getNotificationMessage(language: string, tags: string[]): string;
};

import type { ArticleEntry } from './ArticleEntry';
import type { ActivityWorkoutEntry } from './ActivityWorkoutEntry';
import type { BreathingEntry } from './BreathingEntry';
import type { MeditationEntry } from './MeditationEntry';
import type { WorkoutTemplateEntry } from './WorkoutTemplateEntry';
import type { LinkEntry } from './LinkEntry';
import type { DefaultExploreEntry } from './DefaultExploreEntry';
import type { SubscribableEntry } from './SubscribableEntry';
import type { OnlineMeetingEntry } from './OnlineMeetingEntry';
import type { OfflineMeetingEntry } from './OfflineMeetingEntry';
import type { WebinarEntry } from './WebinarEntry';

export type ExploreEntryUnion =
  | ArticleEntry
  | ActivityWorkoutEntry
  | BreathingEntry
  | MeditationEntry
  | WorkoutTemplateEntry
  | LinkEntry
  | DefaultExploreEntry
  | SubscribableEntry
  | OnlineMeetingEntry
  | OfflineMeetingEntry
  | WebinarEntry;

export function isArticleEntry(entry: ExploreEntry): entry is ArticleEntry {
  return entry.type === 'article';
}

export function isActivityWorkoutEntry(entry: ExploreEntry): entry is ActivityWorkoutEntry {
  return entry.type === 'activityWorkout';
}

export function isBreathingEntry(entry: ExploreEntry): entry is BreathingEntry {
  return entry.type === 'breathing';
}

export function isMeditationEntry(entry: ExploreEntry): entry is MeditationEntry {
  return entry.type === 'meditation';
}

export function isWorkoutTemplateEntry(entry: ExploreEntry): entry is WorkoutTemplateEntry {
  return entry.type === 'workoutTemplate';
}

export function isLinkEntry(entry: ExploreEntry): entry is LinkEntry {
  return entry.type === 'link';
}

export function isOnlineMeetingEntry(entry: ExploreEntry): entry is OnlineMeetingEntry {
  return entry.type === 'onlineMeeting';
}

export function isOfflineMeetingEntry(entry: ExploreEntry): entry is OfflineMeetingEntry {
  return entry.type === 'offlineMeeting';
}

export function isWebinarEntry(entry: ExploreEntry): entry is WebinarEntry {
  return entry.type === 'webinar';
}

export function createExploreEntry(json?: Partial<ExploreEntry>): ExploreEntry {
  return makeAutoObservable({
    ...createExploreContent(json),
    backgroundImage: json?.backgroundImage ? createMedia(json.backgroundImage) : undefined,
    backgroundVideos: (json?.backgroundVideos ?? []).map((m: any) => createMedia(m)),
    notificationMessages: (json?.notificationMessages ?? []).map((n: any) => createNotificationMessage(n)),
    quotes: (json?.quotes ?? []).map((q: any) => createLocalizedValue(q)),
    orientation: json?.orientation ?? 'landscape',
    categoryData: undefined as ExploreCategory[] | undefined,
    spotifyPlaylistLink: json?.spotifyPlaylistLink,
    visible: false,

    get tagString(): string {
      return this.tagConditions.map((t) => t.tagString).join(' | ');
    },

    get media(): Media | undefined {
      return this.backgroundImage ?? this.images[0];
    },

    getNotificationTitle(language: string, tags: string[]): string {
      return (
        this.notificationMessages.find((m) => m.tagCondition.matches(tags))?.title?.find((l) => l.lang === language)
          ?.value ??
        String(translate(`explore.scheduler.notificationTitle.${this.type}`, { defaultValue: this.getName(language) }))
      );
    },

    getNotificationMessage(language: string, tags: string[]): string {
      return (
        this.notificationMessages.find((m) => m.tagCondition.matches(tags))?.message?.find((l) => l.lang === language)
          ?.value ?? String(translate(`explore.scheduler.notificationTitle.${this.type}`))
      );
    },
  });
}

export const ExploreEntryApi = {
  count(request?: Partial<ExploreEntryQueryRequest>): Promise<number> {
    return HttpBackend.get(
      '/coach/explore/v3/entries/count',
      v1ParamsToV3Params(Object.assign({}, request ?? {}, { contentType: 'entry' })),
    ).then((res) => res ?? 0);
  },
};
